import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Mail, Phone, Trash2, X } from "lucide-react";
import Shell, { Breadcrumbs } from "@/components/Shell";
import { Card, Btn, Input, Textarea, Label, Spinner, IconBtn } from "@/components/ui-kit";
import api from "@/lib/api";
import { useConfirm } from "@/lib/confirm";
import { formatApiError } from "@/lib/format";

const ContactModal = ({ open, onClose, contact, onSave }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  useEffect(() => {
    if (open) setForm(contact || { name: "", email: "", phone: "" });
  }, [open, contact]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in" data-testid="supplier-contact-modal">
      <div className="absolute inset-0 bg-zinc-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-start justify-between p-6 pb-2">
          <h2 className="font-display text-2xl font-semibold">{contact ? "Edit Contact" : "Add Contact"}</h2>
          <IconBtn onClick={onClose}><X className="w-4 h-4" /></IconBtn>
        </div>
        <form
          onSubmit={async (e) => { e.preventDefault(); await onSave(form); onClose(); }}
          className="p-6 pt-2 space-y-4"
        >
          <div><Label required>Name</Label><Input required data-testid="supplier-contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" data-testid="supplier-contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Phone</Label><Input data-testid="supplier-contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
            <Btn type="submit" data-testid="supplier-contact-save-btn">Save</Btn>
          </div>
        </form>
      </div>
    </div>
  );
};

const SupplierForm = ({ mode = "new" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [form, setForm] = useState({ name: "", type: "", website: "", phone: "", notes: "" });
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editContact, setEditContact] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  const loadSupplier = async () => {
    const r = await api.get(`/suppliers/${id}`);
    setForm({
      name: r.data.name || "",
      type: r.data.type || "",
      website: r.data.website || "",
      phone: r.data.phone || "",
      notes: r.data.notes || "",
    });
    setContacts(r.data.contacts || []);
  };

  useEffect(() => {
    if (mode === "edit") loadSupplier().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      if (mode === "new") {
        const { data } = await api.post("/suppliers", form);
        navigate(`/suppliers/${data.id}/edit`);
      } else {
        await api.put(`/suppliers/${id}`, form);
        navigate("/suppliers");
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveContact = async (data) => {
    if (editContact && editContact !== "new") {
      await api.put(`/suppliers/${id}/contacts/${editContact.id}`, data);
    } else {
      await api.post(`/suppliers/${id}/contacts`, data);
    }
    await loadSupplier();
  };

  const deleteContact = async (cid) => {
    if (!(await confirm({ title: "Delete this contact?", confirmText: "Delete", destructive: true }))) return;
    await api.delete(`/suppliers/${id}/contacts/${cid}`);
    await loadSupplier();
  };

  if (loading) return <Shell><div className="flex justify-center py-20"><Spinner /></div></Shell>;

  return (
    <Shell>
      <Breadcrumbs items={[{ to: "/suppliers", label: "Suppliers" }, { label: mode === "new" ? "New Supplier" : form.name || "Edit Supplier" }]} />
      <h1 className="font-display text-4xl font-bold text-zinc-950 mb-1">{mode === "new" ? "New Supplier" : form.name || "Edit Supplier"}</h1>
      <p className="text-sm text-zinc-500 mb-8">{mode === "new" ? "Add a new supplier to your list" : "Update supplier details"}</p>

      <div className="grid lg:grid-cols-3 gap-4 max-w-6xl">
        <Card className="p-6 lg:col-span-2">
          <form onSubmit={submit} className="space-y-4" data-testid="supplier-form">
            <h2 className="font-display text-lg font-semibold text-zinc-950">Supplier Details</h2>
            <div>
              <Label required>Name</Label>
              <Input required data-testid="supplier-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Type of Supplier</Label>
              <Input
                data-testid="supplier-type-input"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                placeholder="e.g. Print, Finishing, Photography, Stock"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Web Address</Label>
                <Input
                  data-testid="supplier-website-input"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="www.brightsea.co.uk"
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input data-testid="supplier-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} data-testid="supplier-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2 pt-2">
              <Btn type="submit" disabled={saving} data-testid="supplier-save-btn">
                {mode === "new" ? "Create Supplier" : "Update Supplier"}
              </Btn>
              <Btn type="button" variant="secondary" onClick={() => navigate(-1)} data-testid="supplier-cancel-btn">Cancel</Btn>
            </div>
          </form>
        </Card>

        <Card className="p-6 lg:col-span-1 h-fit">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold text-zinc-950">Contacts</h2>
            {mode === "edit" && (
              <Btn size="sm" onClick={() => { setEditContact("new"); setContactModalOpen(true); }} data-testid="supplier-add-contact-btn">
                <Plus className="w-3.5 h-3.5" />Add
              </Btn>
            )}
          </div>
          {mode === "new" ? (
            <p className="text-sm text-zinc-500 italic">Save the supplier first, then add contacts here.</p>
          ) : contacts.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-6">No contacts yet</p>
          ) : (
            <div className="space-y-2">
              {contacts.map((ct) => (
                <div key={ct.id} data-testid={`supplier-contact-${ct.id}`} className="p-3 border border-zinc-100 rounded-lg">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-zinc-950">{ct.name}</p>
                    <div className="flex gap-1">
                      <Btn size="sm" variant="secondary" onClick={() => { setEditContact(ct); setContactModalOpen(true); }} data-testid={`supplier-contact-edit-${ct.id}`}>Edit</Btn>
                      <IconBtn variant="destructive" onClick={() => deleteContact(ct.id)} data-testid={`supplier-contact-delete-${ct.id}`}><Trash2 className="w-4 h-4" /></IconBtn>
                    </div>
                  </div>
                  {ct.email && <p className="text-xs text-zinc-500 mt-1 inline-flex items-center gap-1"><Mail className="w-3 h-3" />{ct.email}</p>}
                  {ct.phone && <p className="text-xs text-zinc-500 mt-0.5 inline-flex items-center gap-1"><Phone className="w-3 h-3" />{ct.phone}</p>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <ContactModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        contact={editContact && editContact !== "new" ? editContact : null}
        onSave={saveContact}
      />
    </Shell>
  );
};

export default SupplierForm;
